import {
    AfterViewInit,
    Component,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal,
    ViewChild
} from "@angular/core";
import { MatSidenav } from "@angular/material/sidenav";
import { ActivatedRoute } from "@angular/router";
import { SelectOption } from "@app/shared/components/dsn/custom-select/custom-select.component";
import { NandaChatComponent } from "@app/shared/components/nanda-chat/nanda-chat.component";
import { NandaIconComponent } from "@app/shared/components/nanda-icon/nanda-icon.component";
import { ReportFeedbackComponent } from "@app/shared/components/report-feedback/report-feedback.component";
import { TableProjectionComponent } from "@app/shared/components/table-projection/table-projection.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";
import { ChatNandaService } from "@app/shared/services/chat-nanda.service";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { DrawerService } from "@app/shared/services/drawer.service";
import { ReportNotesNavigationService } from "@app/shared/services/report-notes-navigation.service";
import { VisualizationSortService } from "@app/shared/services/visualization-sort.service";
import { ReportPreferenceService } from "@arch/domain/application/services";
import { EventsService } from "@arch/domain/application/services/events.service";
import {
    CompanyEntity,
    ReportEntity,
    ScenarioEntity
} from "@arch/domain/entity";
import { PopoverComponent } from "@ngx-popovers/popover";
import { ReportBeachmarkingSectionComponent } from "../../components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportDashboardSidenavListComponent } from "../../components/report-dashboard-sidenav-list/report-dashboard-sidenav-list.component";
import { ReportHeaderComponent } from "../../components/report-header/report-header.component";
import { ReportPerformanceComponent } from "../../components/report-performance/report-performance.component";
import {
    EventChangeComparationParams,
    EventChangePeriodParams,
    ReportToolbarComponent
} from "../../components/report-toolbar/report-toolbar.component";
@Component({
    selector: "app-desktop-projection",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        ReportHeaderComponent,
        ReportToolbarComponent,
        ReportBeachmarkingSectionComponent,
        ReportPerformanceComponent,
        ReportDashboardSidenavListComponent,
        ReportFeedbackComponent,
        NandaChatComponent,
        NandaIconComponent,
        TableProjectionComponent
    ],
    templateUrl: "./desktop.component.html",
    styleUrl: "./desktop.component.scss"
})
export class DesktopProjectionComponent implements AfterViewInit {
    @ViewChild("drawer", { static: false }) drawer: MatSidenav;
    @ViewChild(PopoverComponent) popoverOrder: PopoverComponent;

    @Input({ required: true }) loading: boolean;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: false }) set accordionState(value: string) {
        this.accordionStateSignal.set(value);
    }

    @Input() previewMode?: boolean = false;
    private readonly accordionStateSignal = signal<string>(this.accordionState);
    @Output() handleChangePeriod: EventEmitter<EventChangePeriodParams> =
        new EventEmitter();
    @Output()
    handleChangeComparation: EventEmitter<EventChangeComparationParams> =
        new EventEmitter();
    @Output() handleResetPeriod: EventEmitter<void> = new EventEmitter();
    @Output() handleChangeBudget: EventEmitter<ScenarioEntity> =
        new EventEmitter();
    @Output() handleResetBudget: EventEmitter<void> = new EventEmitter();
    @Output() handleChangeProjection: EventEmitter<ScenarioEntity> =
        new EventEmitter();
    @Output() handleResetProjection: EventEmitter<void> = new EventEmitter();
    @Output() handleChangeGoals: EventEmitter<void> = new EventEmitter();
    @Output() handleResetGoals: EventEmitter<void> = new EventEmitter();

    public readonly preferenceService = inject(ReportPreferenceService);
    public readonly accountingPlanService = inject(AccountingPlanService);
    private drawerService = inject(DrawerService);
    private readonly deviceService = inject(DeviceService);
    public readonly chatNandaService = inject(ChatNandaService);
    private readonly reportNotesNavigationService = inject(
        ReportNotesNavigationService
    );
    private readonly visualizationSortService = inject(
        VisualizationSortService
    );
    private readonly eventsService = inject(EventsService);
    public isMobile = this.deviceService.getIsMobile();

    public hasBenchmarking: boolean = true;

    public sectionStates: { [key: string]: boolean } = {
        SectionBenchmarking: true,
        SectionPerformance: true,
        SectionNavigationTips: true,
        SectionIndicators: true,
        SectionFeedback: true
    };

    declare public selectedOrder: string;
    public orderOptions = this.visualizationSortService.sortOptions;

    private readonly route = inject(ActivatedRoute);
    private readonly contactService = inject(ContactService);
    public contact = this.contactService.get();
    constructor() {
        const { orderBy } = this.route.snapshot.queryParams;
        this.selectedOrder = orderBy || "relevance";
        effect(() => {
            const accordionState = this.accordionStateSignal();
            this.onListenerAccordion(accordionState);
        });
    }

    ngAfterViewInit() {
        this.drawerService.drawerState$.subscribe((state) => {
            if (this.drawer) {
                if (state) {
                    this.drawer.open();
                } else {
                    this.drawer.close();
                }
            }
        });

        if (this.drawer) {
            this.drawer.openedChange.subscribe((opened) => {
                if (!opened) {
                    this.drawerService.closeDrawer();
                }
            });
        }
    }

    public toggleSection(
        event: boolean,
        index?: number,
        sectionId?: string
    ): void {
        if (index !== undefined) {
            Object.keys(this.sectionStates).forEach((key) => {
                this.sectionStates[key] = false;
            });

            if (event) {
                this.sectionStates[sectionId] = true;
            }
        }
    }

    public toggleDrawer(): void {
        if (this.drawer) {
            this.drawerService.toggleDrawer();
        }
    }

    public onChangePeriod(event: EventChangePeriodParams): void {
        this.handleChangePeriod.emit(event);
    }

    private onListenerAccordion(accodionId: any): void {
        if (!accodionId) return;
        Object.keys(this.sectionStates).forEach((key) => {
            this.sectionStates[key] = false;
        });
        this.sectionStates[accodionId] = true;
    }

    public onChangeComparation(event: EventChangeComparationParams): void {
        this.handleChangeComparation.emit(event);
    }

    public handleNavigation(event: {
        id: string;
        sectionId?: string;
        visualizationId?: string;
    }): void {
        this.drawerService.closeDrawer();
        Object.keys(this.sectionStates).forEach((key) => {
            this.sectionStates[key] = false;
        });
        this.sectionStates[event.id] = true;
        if (event.visualizationId) {
            setTimeout(() => {
                const element = document.getElementById(event.visualizationId);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "start"
                    });
                }
            }, 100);
        }
    }

    public onOrderChange(option: SelectOption): void {
        this.selectedOrder = option.value;
    }

    public get selectedOrderLabel(): string {
        const option = this.orderOptions.find(
            (opt) => opt.value === this.selectedOrder
        );
        return option?.label || this.selectedOrder;
    }

    public get sortedVisualizations() {
        if (!this.report?.visualizations?.visualzations) {
            return [];
        }

        return this.visualizationSortService.sortVisualizations(
            this.report.visualizations.visualzations,
            this.selectedOrder
        );
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
        this.reportNotesNavigationService.setHasBenchmarkingData(
            hasBenchmarkingData
        );
    }

    public onOpenChat(): void {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.abrir-nanda`
        );
    }
}
